import { ActionType, FullAction } from '@groove/api/gateway/v1/actionCompose';

import { ErrorMessage, SourceType } from './types';

export const BASE_URL =
  process.env.WEB_APPLICATION_BASE_URL || process.env.NX_FEBES_URL || '';
export const SCHEDULER_SETTINGS_URL = `${
  process.env.WEB_APPLICATION_BASE_URL || process.env.NX_FEBES_URL
}/settings?optionsGroup=scheduler`;
export const DIALER_SETTINGS_URL = `${
  process.env.WEB_APPLICATION_BASE_URL || process.env.NX_FEBES_URL
}/settings?activeTab=my_settings&optionsGroup=phoneNumbers`;
export const VOICEMAIL_SETTINGS = `${
  process.env.WEB_APPLICATION_BASE_URL || process.env.NX_FEBES_URL
}/settings?optionsGroup=voicemailRecording`;
export const LINKEDIN_ACTIONS_URL =
  'https://www.linkedin.com/sales/widget/profile-matching';
export const GMAIL_EMAIL_HEADER_CLASS = '.aeH';
export const APP_MODULE_ROOT_CLASS = 'div[class^="webapp-ModuleRoot-root"]';
export const COMPOSE_PANE_IFRAME_NAME = 'febes-action-compose';
export const COMPOSE_PANE_IFRAME_NATIVE_NAME = 'actionCompose';
export const LIGHT_GREY = 'rgba(0, 0, 0, 0.12)';
export const GROOVE_IFRAME_ROOT = 'groove-actions-iframe-root';
export const TASK_PANE_LOCAL_KEY = 'actionComposeTaskPane';
export const VALID_EMAIL_REGEX = /^.+@.+\.[\w-]{2,4}$/;

export enum SFDC_USER_TYPES {
  CONTACT = 'Contact',
  LEAD = 'Lead',
  ACCOUNT = 'Account',
  OPPORTUNITY = 'Opportunity',
  GROOVEUSER = 'GrooveUser',
  UNKNOWN = 'Unknown',
}
export const TYPES: { [key: string]: ActionType } = {
  EMAIL: 'TEMPLATE',
  CALL: 'CALL',
  IN_MAIL: 'STEP_SUBTYPE_LINKEDIN_INMAIL',
  LINKEDIN_CONNECT: 'STEP_SUBTYPE_LINKEDIN_CONNECT',
  SENDOSO: 'STEP_SUBTYPE_SENDOSO',
  SMS: 'STEP_SUBTYPE_SMS',
  IN_PERSON_VISIT: 'STEP_SUBTYPE_IN_PERSON_VISIT',
  DIRECT_MAIL: 'STEP_SUBTYPE_DIRECT_MAIL',
  OTHER: 'OTHER',
  GENERAL: 'GENERAL',
};

export const TOGGLE_ACTION_COMPOSE_EVENT = 'toggle-action-compose';

export const SF_MAPPING_TYPES: { [key: string]: string } = {
  TEMPLATE: 'sf_email_mapping',
  CALL: 'sf_call_mapping',
  STEP_SUBTYPE_LINKEDIN_INMAIL: 'sf_linkedin_inmail_mapping',
  STEP_SUBTYPE_LINKEDIN_CONNECT: 'sf_linkedin_connect_mapping',
  STEP_SUBTYPE_SMS: 'sf_sms_mapping',
  STEP_SUBTYPE_SENDOSO: 'sf_sendoso_mapping',
  STEP_SUBTYPE_IN_PERSON_VISIT: 'sf_in_person_visit_mapping',
  GENERAL: 'sf_task_mapping',
  OTHER: 'sf_task_mapping',
};
export const SOURCE_TYPE: { [key in SourceType]: SourceType } = {
  FLOW: 'FLOW',
  WORKSPACE: 'WORKSPACE',
  ONE_OFF: 'ONE_OFF',
  PLAY: 'PLAY',
  SUGGESTED_ACTION: 'SUGGESTED_ACTION',
};

export const ErrorType: { [key: string]: string } = {
  UpdateActionError: 'UpdateActionError',
  CreateActionError: 'CreateActionError',
  DeleteActionError: 'DeleteActionError',
  GetActionError: 'GetActionError',
  ExecuteActionError: 'ExecuteActionError',
  UnknownError: 'UnknownError',
};

export const errorMessages = {
  unauthorized: (action: string) =>
    `You’re not authorized to ${action} this action. Please contact your admin for additional assistance.`,
  invalidField: (action: string) =>
    `Unable to ${action} action due to invalid fields.`,
  notFound: (action: string) => `Unable to ${action} action. Action not found.`,
  tryAgain: (action: string) => `Unable to ${action}, please try again.`,
  alreadyExecuted: () => 'Action has already been completed.',
  unknown: () =>
    'Oops! Something went wrong on our end. Please reach out to your support contact.',
};

export const errorMessageMap: { [key: string]: ErrorMessage } = {
  UpdateActionError: {
    400: errorMessages.invalidField('save'),
    404: errorMessages.notFound('save'),
    410: errorMessages.alreadyExecuted(),
    422: errorMessages.tryAgain('save action'),
  },
  CreateActionError: {
    400: errorMessages.invalidField('create'),
  },
  DeleteActionError: {
    404: errorMessages.notFound('delete'),
  },
  GetActionError: {
    401: errorMessages.unauthorized('view'),
    404: errorMessages.notFound('view'),
  },
  ExecuteActionError: {
    400: errorMessages.invalidField('execute'),
    404: errorMessages.notFound('execute'),
    410: errorMessages.alreadyExecuted(),
    422: errorMessages.tryAgain('log this action to Salesforce'),
  },
  UnknownError: {
    500: 'Oops! Something went wrong on our end. Please reach out to your support contact.',
  },
};

type ActionTyping = {
  value: ActionType;
  label: string;
};

export type ActionTypings = ActionTyping[];

export const ACTION_TYPES: ActionTypings = [
  {
    value: 'GENERAL',
    label: 'Task',
  },
  {
    value: 'TEMPLATE',
    label: 'Email',
  },
  {
    value: 'CALL',
    label: 'Call',
  },
  {
    value: 'STEP_SUBTYPE_SMS',
    label: 'SMS',
  },
  {
    value: 'STEP_SUBTYPE_LINKEDIN_CONNECT',
    label: 'LinkedIn Connect',
  },
  {
    value: 'STEP_SUBTYPE_LINKEDIN_INMAIL',
    label: 'LinkedIn InMail',
  },
  {
    value: 'STEP_SUBTYPE_SENDOSO',
    label: 'Sendoso',
  },
  {
    value: 'STEP_SUBTYPE_IN_PERSON_VISIT',
    label: 'In Person Visit',
  },
];

export type ActionHash = { [key in ActionType]: string };

export const ACTION_HASH: ActionHash = {
  TEMPLATE: 'Email Action',
  CALL: 'Call Action',
  STEP_SUBTYPE_LINKEDIN_INMAIL: 'LinkedIn InMail',
  STEP_SUBTYPE_LINKEDIN_CONNECT: 'LinkedIn Connect',
  STEP_SUBTYPE_SENDOSO: 'Sendoso Action',
  STEP_SUBTYPE_SMS: 'SMS Message',
  STEP_SUBTYPE_IN_PERSON_VISIT: 'In Person Visit',
  STEP_SUBTYPE_DIRECT_MAIL: 'Direct Mail',
  OTHER: 'Other Action',
  GENERAL: 'General Task',
  WORKSPACE_ACCOUNT_TASK: 'Workspace Task',
  UPDATE_SALESFORCE: 'Update Salesforce Values',
};

export const emptyAction: FullAction = {
  id: '',
  type: 'GENERAL',
  accountId: null,
  accountName: null,
  assignee: null,
  bccRecipients: null,
  body: null,
  ccRecipients: null,
  creatorId: null,
  creator: null,
  crmResults: null,
  crmInstanceUrl: null,
  crmObjectId: null,
  crmTaskType: null,
  description: null,
  dueAt: null,
  dynamicData: {
    loggingForm: { title: null, subtitle: null, fields: [] },
  },
  executeAt: null,
  flow: {
    id: null,
    name: null,
    settings: {
      contact: {
        emailFieldName: null,
        phoneFieldName: null,
      },
      lead: {
        emailFieldName: null,
        phoneFieldName: null,
      },
    },
  },
  fromEmail: null,
  loggingTo: {},
  notes: null,
  personId: null,
  personStepId: null,
  playNodeRunId: null,
  priority: null,
  results: null,
  subject: null,
  suggestedAction: null,
  summary: null,
  template: null,
  toRecipients: null,
  usedAdvanceSS: false,
  who: null,
  workspaceAccountTaskId: null,
  workspaceSObjectTaskId: null,
};

export const INCOMING_MESSAGE_TYPES = {
  LOAD_ACTION_DATA: 'INCOMING_ACTION_COMPOSE/LOAD_ACTION_DATA',
  UPDATE_ACTION_RECORD: 'INCOMING_ACTION_COMPOSE/UPDATE_ACTION_RECORD',
};

export const OUTGOING_MESSAGE_TYPES = {
  SWITCH_ACTION_CANCELED_BY_USER:
    'OUTGOING_ACTION_COMPOSE/SWITCH_ACTION_CANCELED_BY_USER',
};

export type FieldsPivotItemValues = 'standard-fields' | 'custom-fields';
export const fieldsPivotItems = [
  { label: 'Standard fields', value: 'standard-fields' },
  { label: 'Custom fields', value: 'custom-fields' },
];

export const MARK_AS_COMPLETE_TOOLTIP_TEXT =
  'Marks action as complete without logging to Salesforce';

export const SAVE_AND_NEXT_TOOLTIP_TEXT =
  'Save changes and move to the next Action';

export const SAVE_AND_CLOSE_TOOLTIP_TEXT = 'Save changes and close Action';

export const PRIMARY_FLOW_RECIPIENTS_CANNOT_BE_REMOVED_TEXT =
  'Primary flow recipients cannot be removed -';

export const GROOVE_USERS_NOT_LOGGED_TO_SALESFORCE_TEXT =
  'Groove users will not be logged to Salesforce - ';

export const SMS_PHONE_NUMBER_MISSING_MESSAGE =
  'You do not have a phone number assigned to you, please configure in Dialer Settings';
export const SMS_NOT_ENABLED_MESSAGE =
  'You do not have an SMS license. Please contact your admin to have one assigned';

export const EMAIL_GENERATION_TYPES = [
  {
    value: 'COLD_EMAIL',
    label: 'Cold email',
  },
  {
    value: 'FOLLOWUP_EMAIL',
    label: 'Meeting follow-up',
  },
];
export const EMAIL_WRITING_STYLES = [
  {
    value: 'challenger_sale',
    key: 'challenger_sale',
    text: 'Challenger Sale',
    secondaryText: ['For a conversational and insightful approach'],
    hideTooltip: true,
  },
  {
    value: 'clever_poet',
    key: 'clever_poet',
    text: 'Clever Poet',
    secondaryText: ['To craft content with a creative, poetic twist'],
    hideTooltip: true,
  },
  {
    value: 'cxo_pitch',
    key: 'cxo_pitch',
    text: 'Cxo Pitch',
    secondaryText: ['For concise, executive-level pitches'],
    hideTooltip: true,
  },
  {
    value: 'data_driven',
    key: 'data_driven',
    text: 'Data Driven',
    secondaryText: ['For content backed by data and statistics'],
    hideTooltip: true,
  },
];
export const FOLLOW_UP_TONE_TYPES = [
  { value: 'CASUAL', label: 'Casual' },
  { value: 'NEUTRAL', label: 'Neutral' },
  { value: 'FORMAL', label: 'Formal' },
];

export const LOAD_ACTION_COMPOSE = 'ACTION_COMPOSE/LOAD_ACTION_COMPOSE';
export const UPDATE_ACTION_COMPOSE = 'ACTION_COMPOSE/UPDATE_ACTION_COMPOSE';
export const SET_ACTION_COMPOSE_STATE =
  'ACTION_COMPOSE/SET_ACTION_COMPOSE_STATE';
export const SET_MOBO_USER = 'INCOMING_ACTION_COMPOSE/SET_MOBO_USER';

export const PICKLIST_TYPE = 'Task';

export enum ActionComposeEnum {
  'MAXIMIZED',
  'MINIMIZED',
  'CLOSED',
}

export const ACTION_COMPOSE_INJECTION = 'action-compose-injection';

export const COMPLETE_IN_DIALER = 'Please complete call in dialer';
export const DISABLED_CALL_TOOLTIP =
  'Please select a call recipient with a phone number';

export const CONTENT_TYPES = [
  {
    value: 'full_emails',
    key: 'full_emails',
    text: 'Full email',
    metaData: {
      description:
        'Generates a full personalized email, from the greeting to call-to-action',
    },
    hideTooltip: true,
  },
  {
    value: 'openers',
    key: 'openers',
    text: 'Email introduction',
    metaData: {
      description:
        'Generates a personalized email introduction, containing the greeting and hook',
    },
    hideTooltip: true,
  },
];

export const CALL_PROCESSED = 'POST_PROCESSING_DONE';
export const RECIPIENT_NOT_FOUND =
  'The email was not found in Salesforce. Use Omnibar to add it to Salesforce';
export const RECIPIENT_NOT_FOUND_MODAL_TEXT =
  'We couldn’t find a matching Salesforce record for one or more of the recipients you’ve entered. As a result, sending this email will not be logged for those recipients not in Salesforce. Consider creating a Salesforce record for the indicated recipients before sending.';
export const DELETE_ACTION_MODAL_TEXT =
  'Deleting this action cannot be undone. Are you sure you want to proceed';
export const ONE_OFF_ACTION_NOT_SAVED =
  'Was not able to save the new one-off action';
export const NO_CONTENT_SELECTED = 'No content currently selected';
export const FEEDBACK_SUBMISSION_FAILED = 'Unable to submit feedback';
export const REPHRASE_FAILED = 'Unable to rephrase selection. Try again';
export const AI_REPHRASE_ERROR_ID = 'aiRephraseError';
export const AI_REPHRASE_FEEDBACK_ERROR_ID = 'aiRephraseFeedbackError';
export const AI_REPHRASE_PANE_ID = 'REPHRASE';
export const SEND_NOW_ACTION_TEXT =
  'This email will be sent immediately and cannot be undone. Are you sure you want to continue?';
export type ActionState = 'NEW' | 'EXISTING' | 'ERROR';

export type ActionStateHash = { [key in ActionState]: string };

export const ACTION_STATE: ActionStateHash = {
  NEW: 'New',
  EXISTING: 'Existing',
  ERROR: 'Error',
};

export type ActionEvent =
  | 'OPENED'
  | 'SAVED'
  | 'LOGGED'
  | 'NEXT'
  | 'PREV'
  | 'PAUSE_PERSON'
  | 'REMOVE_PERSON'
  | 'SKIP_STEP'
  | 'SKIP_ALL_STEPS'
  | 'LOG_WITHOUT_CALLING'
  | 'LOG_CREATE_FOLLOW_UP'
  | 'LOG_MOVE_TO_ANOTHER_FLOW'
  | 'LOG_MOVE_TO_ANOTHER_FLOW_DROP_DOWN'
  | 'LOG_MOVE_TO_ANOTHER_LOGGED'
  | 'LOG_AND_PAUSE_OPEN'
  | 'LOG_AND_PAUSE_CONFIRM'
  | 'LOG_AND_REMOVE_FROM_FLOW'
  | 'LOG_AND_SKIP_FUTURE'
  | 'LOG_AND_TRY_AGAIN'
  | 'COMPLETE_AND_TRY_AGAIN'
  | 'MARKED_AS_COMPLETE'
  | 'CLOSED'
  | 'GENERATE_COLD_EMAIL'
  | 'GENERATE_FOLLOW_UP'
  | 'SELECT_COLD_EMAIL'
  | 'SELECT_FOLLOW_UP'
  | 'CALL_RESULT_SELECTED'
  | 'DIA_LOCAL_DIAL'
  | 'DIA_CONSTANT_DIAL'
  | 'DIA_RECORDING'
  | 'DIA_KEYPAD_OPENED'
  | 'DIA_KEYPAD_CLOSED'
  | 'DIA_MUTE'
  | 'DIA_VM_DROP_OPENED'
  | 'DIA_VM_DROP_CLOSED'
  | 'DIA_VM_SELECTED'
  | 'DIA_VM_DROP_MANAGE'
  | 'DIA_START_CALL'
  | 'DIA_END_CALL'
  | 'DIA_CALL_AGAIN'
  | 'DIA_CALL_TRANSFER_ICON'
  | 'DIA_CALL_TRANSFER_BUTTON';

export type ActionEventHash = { [key in ActionEvent]: string };

export const MORE_MENU = 'More Menu: ';
export const SPLIT_MENU = 'Log split button: ';

export const ACTION_EVENT: ActionEventHash = {
  OPENED: 'Opened',
  SAVED: 'Save button clicked',
  LOGGED: 'Log button clicked',
  PAUSE_PERSON: 'Pause this person in flow clicked',
  REMOVE_PERSON: 'Remove person from flow clicked',
  SKIP_STEP: 'Skip this step clicked',
  SKIP_ALL_STEPS: 'Skip all call steps clicked',
  LOG_WITHOUT_CALLING: 'Log without calling clicked',
  LOG_CREATE_FOLLOW_UP: 'Log and create follow-up clicked',
  LOG_MOVE_TO_ANOTHER_FLOW: 'Log and move to another flow clicked',
  LOG_MOVE_TO_ANOTHER_FLOW_DROP_DOWN:
    'Log and move to another flow modal: Select a flow dropdown clicked',
  LOG_MOVE_TO_ANOTHER_LOGGED:
    'Log and move to another flow modal: Log button clicked',
  LOG_AND_PAUSE_OPEN: 'Log and pause until clicked',
  LOG_AND_PAUSE_CONFIRM: 'Log and pause until modal: Pause button clicked',
  LOG_AND_REMOVE_FROM_FLOW: 'Log and remove from flow clicked',
  LOG_AND_SKIP_FUTURE: 'Log and skip future call steps clicked',
  LOG_AND_TRY_AGAIN: 'Log and try again clicked',
  COMPLETE_AND_TRY_AGAIN: 'Complete and try again clicked',
  MARKED_AS_COMPLETE: 'Marked as Complete clicked',
  CLOSED: 'Closed',
  NEXT: 'Clicked Next',
  PREV: 'Clicked Previous',
  SELECT_COLD_EMAIL: 'GenAI Email: Cold Email Intent Selected',
  SELECT_FOLLOW_UP: 'GenAI Email: Meeting Follow Up Intent Selected',
  GENERATE_COLD_EMAIL: 'GenAI Email: Generate Cold Email',
  GENERATE_FOLLOW_UP: 'GenAI Email: Generate Smart Follow Up',
  CALL_RESULT_SELECTED: 'Call result selected',
  DIA_LOCAL_DIAL: 'Local Dial button clicked',
  DIA_CONSTANT_DIAL: 'Constant Bridge Dial button clicked',
  DIA_RECORDING: 'Recording button clicked',
  DIA_KEYPAD_OPENED: 'Keypad button opened',
  DIA_KEYPAD_CLOSED: 'Keypad button closed',
  DIA_MUTE: 'Mute button clicked',
  DIA_VM_DROP_OPENED: 'VM Drop menu opened',
  DIA_VM_DROP_CLOSED: 'VM Drop menu closed',
  DIA_VM_SELECTED: 'VM Drop: VM selected from dropdown',
  DIA_VM_DROP_MANAGE: 'VM Drop: Manage voicemail button clicked',
  DIA_START_CALL: 'Call button clicked',
  DIA_END_CALL: 'End call button clicked',
  DIA_CALL_AGAIN: 'Call again button clicked',
  DIA_CALL_TRANSFER_ICON: 'Call transfer Icon clicked',
  DIA_CALL_TRANSFER_BUTTON: 'Call transfer Button clicked',
};

export type ActionSourceHash = { [key in SourceType]: string };

export const ACTION_SOURCE: ActionSourceHash = {
  FLOW: 'Flow',
  WORKSPACE: 'Space',
  ONE_OFF: 'One-Off',
  PLAY: 'Play',
  SUGGESTED_ACTION: 'Suggested Action',
};

export enum ACTION_ORIGIN {
  WEBAPP = 'Webapp',
  GMAIL = 'Gmail',
  EXTENSION = 'Extension',
  CALENDAR = 'Calendar',
  SALESFORCE = 'Salesforce',
  ADDIN = 'Add-In',
  FLOWS = 'Flows',
  DESKTOP = 'Desktop-add-in',
}

export type ActionTypeHash = { [key in ActionType]: string };

export const ACTION_TYPE: ActionTypeHash = {
  TEMPLATE: 'Email',
  CALL: 'Call',
  OTHER: 'Other',
  GENERAL: 'Task',
  STEP_SUBTYPE_SMS: 'Sms',
  STEP_SUBTYPE_DIRECT_MAIL: 'Direct Mail',
  STEP_SUBTYPE_IN_PERSON_VISIT: 'In Person Visit',
  STEP_SUBTYPE_LINKEDIN_CONNECT: 'LinkedIn Connect',
  STEP_SUBTYPE_LINKEDIN_INMAIL: 'LinkedIn InMail',
  STEP_SUBTYPE_SENDOSO: 'Sendoso',
  UPDATE_SALESFORCE: 'Update Salesforce',
  WORKSPACE_ACCOUNT_TASK: 'Workspace',
};

export enum ACTION_VERSION {
  V1 = 'V1',
  V2 = 'V2',
}

export const NO_GROOVE_NUMBER = 'noGrooveNumberError';
export const INVALID_BRIDGE_DIAL = 'invalidBridgeDial';
export const CALL_ERROR_MESSAGE_ID = 'CallErrorMessageId';

export enum SALESFORCE_RECORD_TEXT {
  ACCOUNT = 'Account',
  EMAIL = 'Email',
  CAMPAIGN = 'Campaign',
  CASE = 'Case',
  CLOSE_DATE = 'Close Date',
  COMPANY = 'Company',
  CONTACT = 'Contact',
  DESCRIPTION = 'Description',
  LEAD = 'Lead',
  NO_NAME = '<No Name>',
  ORGANIZATION = 'Organization',
  OPPORTUNITY = 'Opportunity',
  STAGE = 'Stage',
  TITLE = 'Title',
}
export const IMPORT_RULES_FF = 'apply-import-rules-to-action-execution';
export const HIDE_LINKED_IN_ACTIONS_FF = 'hide-linkedIn-actions-from-outlook';

export const LINKEDIN_AUTH_SUCCESS_MESSAGE_TYPE = 'salesNavAuthSuccess';
export const LINKEDIN_ERROR_TYPE = 'tokenError';
export const LINKEDIN_ORIGIN = 'https://www.linkedin.com';
export const LINKEDIN_LISTENER_PIXEL = 'groove-linked-in-listener-pixel';

export const SFDC_WHO_PREFIXES = ['003', '00Q'];

export const INVALID_CUSTOM_FIELDS: { [key: string]: boolean } = {
  Status: true,
  DaScoopComposer__Is_Created_By_Groove__c: true,
  groove_sent_from_flow__c: true,
  Type: true,
  ActivityDate: true,
  Subject: true,
  Description: true,
  Template_Name__c: true,
  TaskSubtype: true,
  DaScoopComposer__vCal_UID__c: true,
  Flow_Name__c: true,
  Groove_Activity_Overdue_By_Days__c: true,
  Groove_Step_Type__c: true,
  Groove_Step_Number__c: true,
  ESI_GES_QC_Segment__c: true,
  Groove_Person_Flow_ID__c: true,
  IsReminderSet: true,
};

export const INSTRUCTIONS_MAP: Record<string, string> = {
  'more-formal': 'Change tone: Formal',
  'more-casual': 'Change tone: Casual',
  'more-concise': 'Shorten it',
  'Make more neutral': 'Change tone: Neutral',
};

// related to SyncWhoData
// any attribute that collides with Groove reserved attributes
// example dascoopcomposer__email_2__c -> email_2__c
// https://clarius.atlassian.net/wiki/spaces/PD/pages/4826529807/Managed+Package+Reserved+Attributes
export const RESERVED_ATTRIBUTES = ['email_2__c'];

export const PRE_PERSONALIZATION_WARNING_MESSAGE =
  'Edits made here in actions will not be reflected in the flow personalization screen, but we have auto-saved your action and you can continue to edit directly within actions.';

export const PRE_PERSONALIZATION_FEATURE_FLAG = 'flow-pre-personalization';
